'use client'

import { Card } from '@/components/ui/card'
import { getAssetKey } from '@/lib/assets/assets'
import styles from './asset-preview.module.scss'
import { FaStar } from 'react-icons/fa6'
import React, { memo, useCallback, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { ArrowDownToLineIcon, HeartIcon } from 'lucide-react'
import { HeartFilledIcon } from '@radix-ui/react-icons'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import client from '@/lib/api/client'
import { useQuery } from '@tanstack/react-query'
import { Skeleton } from '@/components/ui/skeleton'
import { useHover } from '@uidotdev/usehooks'
import { useUserContext } from '@/lib/contexts/UserContext'
import Link from 'next/link'
import useFavorites from '@/lib/hooks/useFavorites'
import { cn, formatBytes } from '@/lib/utils'
import { features } from '@/lib/config'
import useAssetDetail from '@/lib/hooks/useAssetDetail'
import { Asset, AssetSummary } from '@/lib/assets/types'
import isEqual from 'react-fast-compare'
import { Separator } from '@/components/ui/separator' //TODO: Convert TailwindCSS to SCSS

//TODO: Convert TailwindCSS to SCSS

export type AssetPreviewProps = {
  asset: AssetSummary
  className?: string
  onClick?: (asset?: AssetSummary) => void
}

const VideoPreview = memo(({ src, poster, show = false }: { src?: string; poster?: string; show?: boolean }) => {
  const [shown, setShown] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const setVideoRef = useCallback((video: HTMLVideoElement | null) => {
    if (video) {
      const loadedListener = () => {
        setLoaded(true)
        video.removeEventListener('loadeddata', loadedListener)
      }
      video.addEventListener('loadeddata', loadedListener)
    }
  }, [])

  useEffect(() => {
    if (show && !shown) {
      setShown(true)
    }
  }, [show, shown])

  return (
    <>
      {!!src && shown && (
        <video
          data-slot="video-preview"
          ref={setVideoRef}
          className={cn(styles.videoPreview, 'tw-transition-opacity tw-duration-300', {
            'tw-opacity-0': !loaded || !show,
            'tw-opacity-100': loaded && show,
          })}
          autoPlay
          loop
          muted
          playsInline
          poster={poster ?? ''}
          src={src}
        ></video>
      )}
    </>
  )
}, isEqual)

VideoPreview.displayName = 'VideoPreview'

const AssetPreview = ({ asset, className, onClick = () => {} }: AssetPreviewProps) => {
  const { mainCategorySlug, categorySlug, subCategorySlug, contentSlug } = asset
  const assetKey = getAssetKey(asset)
  const { addFavorite, removeFavorite, isFavorite: isFavoriteFn } = useFavorites()
  const isFavorite = isFavoriteFn(assetKey)

  const [ref, hoveringCard] = useHover()
  const { userToken, isAuthenticating } = useUserContext()

  const [shouldLoadDownloadOptions, setShouldLoadDownloadOptions] = useState(false)

  const [isHoveringDownloadOptions, setIsHoveringDownloadOptions] = useState(false)

  const hovering = hoveringCard || isHoveringDownloadOptions

  const { data: assetDetail, isLoading: isLoadingDownloadOptions } = useQuery({
    queryKey: ['asset', assetKey],
    queryFn: () =>
      client
        .get<Asset>(`/assets/${assetKey}`, {
          params: {
            type: asset.__typename,
            main_category: mainCategorySlug,
            category: categorySlug,
            sub_category: subCategorySlug,
            content: contentSlug,
          },
        })
        .then(({ data }) => data),
    enabled: shouldLoadDownloadOptions,
  })

  const { isLightBackground, imagePreview, videoPreview, is4K, assetDetailUrl } = useAssetDetail({ asset, userToken })

  const { downloadOptions, dispatchAssetDownloadEvent } = useAssetDetail({
    asset: assetDetail,
    userToken,
  })

  const handleDownloadOptionsOpenChange = useCallback((open: boolean) => {
    if (open) {
      setShouldLoadDownloadOptions(true)
    }
    setIsHoveringDownloadOptions(open)
  }, [])

  return (
    <Card
      data-slot="asset-preview"
      className={cn(
        'asset-preview',
        styles.card,
        '!tw-max-w-full',
        {
          '!tw-bg-[var(--light-assets-background-color)]': isLightBackground,
        },
        className
      )}
      onClick={onClick}
      ref={ref}
    >
      {/*<div className={styles.image} style={{*/}
      {/*  backgroundImage: `url(${asset.image ?? ''})`,*/}
      {/*  backgroundRepeat: 'no-repeat',*/}
      {/*  backgroundSize: 'cover',*/}
      {/*  backgroundPosition: 'center',*/}
      {/*}}></div>*/}

      <div
        className={cn(styles.body, {
          [styles.bodyHover]: hovering,
        })}
      >
        {!!imagePreview && (
          <picture className={styles.picture}>
            <source srcSet={imagePreview ?? ''} type="image/png" />
            <img className={styles.image} src={imagePreview ?? ''} alt={asset.title || 'Asset preview'} />
          </picture>
        )}

        {/* Video preview */}
        <VideoPreview
          src={videoPreview ?? undefined}
          poster={imagePreview ?? undefined}
          show={hovering && !!videoPreview}
        />

        <div
          className={cn(styles.content, {
            [styles.contentHover]: hovering,
          })}
        >
          {/* Overlay */}
          <div className="tw-absolute tw-bottom-0 tw-flex tw-w-full tw-flex-row tw-items-center tw-space-x-2 tw-p-4">
            <h6
              className={cn('body_4 tw-flex-1', {
                '!tw-text-black': isLightBackground,
              })}
            >
              <Link
                className="hover:tw-underline"
                href={assetDetailUrl ?? '#'}
                onClick={(e) => {
                  e.stopPropagation()
                }}
              >
                {asset.title}
              </Link>
            </h6>
            <div className="tw-flex tw-flex-row tw-items-center tw-space-x-1">
              {/* Favorite button */}
              {features.favorites && (
                <Button
                  title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                  variant="transparent"
                  size="icon"
                  className={cn('!tw-p-1', {
                    '!tw-text-black': isLightBackground,
                  })}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    isFavorite ? removeFavorite(assetKey) : addFavorite(assetKey)
                  }}
                >
                  {isFavorite ? <HeartFilledIcon className="tw-h-4 tw-w-4" /> : <HeartIcon className="tw-h-4 tw-w-4" />}
                </Button>
              )}

              {/* Download button */}
              <HoverCard openDelay={0} onOpenChange={handleDownloadOptionsOpenChange}>
                <HoverCardTrigger asChild>
                  <Button
                    title="Download"
                    variant="transparent"
                    size="icon"
                    className={cn('!tw-p-1', {
                      '!tw-text-black': isLightBackground,
                    })}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      // TODO: Download default option
                    }}
                  >
                    <ArrowDownToLineIcon className="tw-h-4 tw-w-4" />
                  </Button>
                </HoverCardTrigger>
                <HoverCardContent
                  className="tw-w-auto tw-max-w-min tw-border-0 !tw-bg-[var(--Primary)] tw-p-2 tw-shadow-none"
                  side="top"
                  sideOffset={5}
                  align="start"
                  alignOffset={-16}
                >
                  <div className="tw-flex tw-flex-col tw-items-start tw-space-y-2">
                    {(isLoadingDownloadOptions || isAuthenticating) && (
                      <>
                        <Skeleton className="tw-h-4 tw-w-16" />
                        <Skeleton className="tw-h-4 tw-w-16" />
                        <Skeleton className="tw-h-4 tw-w-16" />
                      </>
                    )}

                    {!isLoadingDownloadOptions && !isAuthenticating && (
                      <>
                        {downloadOptions.map((option, index) => (
                          <Link
                            key={`${option.url}-${index}`}
                            href={option.url}
                            prefetch={false}
                            className="tw-opacity-50 hover:tw-opacity-100"
                            onClick={(e) => {
                              e.stopPropagation()
                              dispatchAssetDownloadEvent()
                            }}
                          >
                            <div className="tw-flex tw-items-center tw-space-x-2">
                              <span className="body_4 tw-whitespace-nowrap tw-rounded tw-border tw-border-[var(--Primary-100)] tw-px-1">
                                {option.resolution}
                              </span>
                              <span className="tw-whitespace-nowrap">{option.format}</span>
                              {!!option.size && (
                                <>
                                  <Separator orientation={'vertical'} className="!tw-h-3" />
                                  <span className="tw-whitespace-nowrap tw-text-sm">{formatBytes(option.size)}</span>
                                </>
                              )}
                            </div>
                          </Link>
                        ))}
                      </>
                    )}
                  </div>
                </HoverCardContent>
              </HoverCard>
            </div>
          </div>

          {+asset.level > 0 && (
            <span className={styles.premiumIcon} title="Premium asset">
              <FaStar className={styles.starIcon} />
            </span>
          )}

          {is4K && (
            <span
              className={cn(
                'body_4 tw-absolute tw-right-4 tw-top-4 tw-rounded tw-border tw-border-[var(--Primary-100)] tw-px-1',
                {
                  '!tw-border-black': isLightBackground,
                  '!tw-text-black': isLightBackground,
                }
              )}
            >
              4K
            </span>
          )}
        </div>
      </div>
    </Card>
  )
}

export { AssetPreview }
